import { useToast } from '@chakra-ui/react';
import { useSettingsApi } from '@src/modules/settings/api/settings';
import { useUserInfo } from '@transport-insights/uikit';
import { useMutation, useQuery, useQueryClient } from 'react-query';

export function useNews() {
  const api = useSettingsApi();
  const { isLoggedIn } = useUserInfo();

  return useQuery(
    ['news', isLoggedIn],
    async () => api.getNews(),
    {
      enabled: isLoggedIn,
    },
  );
}

export function useSettingsConfig() {
  const api = useSettingsApi();

  return useQuery(
    ['settings-config'],
    async () => api.getConfig(),
  );
}

export function useGetContent(contentType) {
  const api = useSettingsApi();

  return useQuery(
    [`content-${contentType}`],
    async () => api.getContent(contentType),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    },
  );
}

// CMS Admin Functions

const contentTypeMap = {
  'coming-soon': 8,
  'new-features': 9,
  'tips-and-tricks': 10,
};

function getContentId(type) {
  return contentTypeMap[type] ?? null;
}

export function useGetAdminContent(contentType) {
  const api = useSettingsApi();

  if (!(contentType in contentTypeMap)) {
    throw new Error(`Unknown content type: ${contentType}`);
  }

  const contentId = getContentId(contentType);

  return useQuery(
    [`content-${contentId}`],
    async () => api.getAdminContent(contentId),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    },
  );
}

export const useCreateContentEntry = (contentType) => {
  const api = useSettingsApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const contentId = getContentId(contentType);

  return useMutation(
    (contentEntry) => api.createContentEntry(contentEntry).then(({ data }) => data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`content-${contentId}`);
        toast({
          title: 'Success',
          description: 'Entry was successfully created.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.response.data.Message ? error.response.data.Message : 'Entry could not be created. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );
};

export const useUpdateContentEntry = (contentType) => {
  const api = useSettingsApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const contentId = getContentId(contentType);

  return useMutation(
    (contentEntry) => api.updateContentEntry(contentEntry).then(({ data }) => data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`content-${contentId}`);
        toast({
          title: 'Success',
          description: 'Entry was successfully updated.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.response.data.Message ? error.response.data.Message : 'Entry could not be updated. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );
};

export const useDeleteEntry = (contentType) => {
  const api = useSettingsApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const contentId = getContentId(contentType);

  return useMutation(
    (id) => api.deleteContentEntry(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`content-${contentId}`);
        toast({
          title: 'Success',
          description: 'Entry was successfully deleted.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.response.data.Message ? error.response.data.Message : 'Entry could not be deleted. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );
};
